import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade'; // Import Fade for animation
import Chip from '@mui/material/Chip'; // Import Chip for default inputs

// Import necessary MUI icons
import CloseIcon from '@mui/icons-material/Close';
// Removed react-icons import for RiChatSmile2Line
// Removed PersonOutlineIcon import
import queryGraphql from '../helpers/ApiInterface'; // User added import
import { useAuth } from '../helpers/AuthProvider'; // User added import

// Define the structure of a message object
interface Message {
  id: number;
  text: string;
  sender: 'user' | 'agent';
}

// --- Single Chatbot Widget Component (MUI Version) ---
const MiniBot: React.FC = () => {
  // State for modal visibility
  const [isChatbotOpen, setIsChatbotOpen] = useState<boolean>(false);
  // State for the input field value
  const [inputValue, setInputValue] = useState<string>('');
  // State for the conversation messages
  const [messages, setMessages] = useState<Message[]>([]);
  // State to indicate if waiting for API response
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Ref for the chat messages area to enable auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatAreaRef = useRef<HTMLDivElement>(null);

  // --- Default Input Suggestions ---
  const defaultInputs = ['What is Intellicore?', 'How to create a cluster?'];

  const fontFamily = '"Gilroy-Regular", Arial, sans-serif';
  const { user } = useAuth();

  const openChatbot = () => {
    setIsChatbotOpen(true);
    if (messages.length === 0) {
        setMessages([{ id: Date.now(), text: "Hi there! How can I help you today?", sender: 'agent' }]);
    }
  };

  const closeChatbot = () => {
    setIsChatbotOpen(false);
  };

  const scrollToBottom = () => {
     setTimeout(() => {
        if (chatAreaRef.current) {
             chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
        }
     }, 0);
  };

  useEffect(() => {
    if(isChatbotOpen) {
        scrollToBottom();
    }
  }, [messages, isChatbotOpen]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText ?? inputValue.trim();
    if (textToSend === '' || isLoading) {
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      text: textToSend,
      sender: 'user',
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);

    const messageForApi = textToSend;
    if (!messageText) {
        setInputValue('');
    }
    setIsLoading(true);

    try {
      const response = await queryGraphql(
          `query Query($inputQuery: String!) {
             getChatResults(inputQuery: $inputQuery)
           }`,
          { inputQuery: messageForApi }
        );
      console.log("GraphQL Response Object:", response);
      if (response && typeof response.getChatResults === 'string') {
        const apiResponseText = response.getChatResults;
        const agentResponse: Message = {
          id: Date.now() + 1,
          text: apiResponseText,
          sender: 'agent',
        };
        setMessages((prevMessages) => [...prevMessages, agentResponse]);
      } else {
        console.error("Unexpected API response structure:", response);
        throw new Error("Sorry, I received an unexpected response.");
      }
    } catch (error) {
      console.error("API call failed:", error);
      const errorResponse: Message = {
        id: Date.now() + 1,
        text: error instanceof Error ? error.message : "Sorry, I couldn't process that. Please try again.",
        sender: 'agent',
      };
      setMessages((prevMessages) => [...prevMessages, errorResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const modalStyle = {
    position: 'absolute' as 'absolute',
    bottom: { xs: '2vh', md: '2vh' }, // Converted from 20px, 40px
    right: { xs: '2vw', md: '2vw' },  // Converted from 20px, 40px
    width: "24vw", // Already vw
    height: "72vh", // Already vh
    bgcolor: 'background.paper',
    border: '1px solid #e0e0e0', // Kept 1px
    borderRadius: 4,
    boxShadow: 24,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    outline: 'none',
    fontFamily: fontFamily,
    p: 0,
  };

  return (
    <>
      {!isChatbotOpen && (
        <Box sx={{ 
            position: 'fixed', 
            bottom: '3.3vh', // Converted from 20px
            right: '3.3vw', // Converted from 20px
            zIndex: 1000 
        }}>
          <Button
            variant="contained"
            onClick={openChatbot}
            aria-label="Open chat"
            sx={{
              borderRadius: '50%',
              width: '7vh',  // Converted from 60px
              height: '7vh', // Converted from 60px
              p: 0,
              minWidth: 'auto',
              bgcolor: 'common.black',
              color: 'common.white',
              '&:hover': { bgcolor: 'grey.900' },
              fontFamily: fontFamily,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              src="/assets/bot-trigger.svg"
              alt="Open Chatbot"
              sx={{
                width: '4vh', // Converted from 35px (relative to button size)
                height: '4vh', // Converted from 35px (relative to button size)
              }}
            />
          </Button>
        </Box>
      )}

      <Modal
        open={isChatbotOpen}
        onClose={closeChatbot}
        aria-labelledby="chatbot-modal-title"
        aria-describedby="chatbot-modal-description"
        closeAfterTransition
        slotProps={{ backdrop: { style: { backgroundColor: 'transparent' } } }}
      >
        <Fade in={isChatbotOpen} timeout={300}>
          <Paper sx={modalStyle} elevation={8}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5, // Theme spacing
                borderBottom: '1px solid', // Kept 1px
                borderColor: 'divider',
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}> {/* Theme spacing */}
                <Avatar
                  alt="AI Agent Logo"
                  src="/Logo.svg"
                  sx={{ width: '3.5vh', height: '3.5vh' }} // Converted from 32px
                />
                <Typography id="chatbot-modal-title" variant="subtitle1" fontWeight="medium">
                  Intellicore
                </Typography>
              </Box>
              <IconButton onClick={closeChatbot} size="small" aria-label="close chatbot">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              id="chatbot-modal-description"
              ref={chatAreaRef}
              sx={{
                flexGrow: 1,
                p: 2, // Theme spacing
                overflowY: 'auto',
                bgcolor: 'grey.50',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                msOverflowStyle: 'none',
                borderRadius: 2, // Theme spacing
              }}
            >
              <Stack spacing={2}> {/* Theme spacing */}
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.sender === 'user' ? 'flex-end' : 'flex-start',
                    }}
                  >
                    <Box sx={{ maxWidth: '80%' }}>
                      {message.sender === 'agent' ? (
                        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 5, bgcolor: 'background.paper' }}> {/* Theme spacing & unitless */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}> {/* Theme spacing */}
                            <Avatar
                              alt="AI Agent"
                              src="/Logo.svg"
                              sx={{ width: '2.2vh', height: '2.2vh' }} // Converted from 20px
                            />
                            <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                              Intellicore • AI Agent
                            </Typography>
                          </Box>
                          <Typography variant="body2">{message.text}</Typography>
                        </Paper>
                      ) : (
                        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 5, bgcolor: 'common.black', color: 'common.white' }}> {/* Theme spacing & unitless */}
                           <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 0.5 }}> {/* Theme spacing */}
                             <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'grey.400' }}>
                               {user?.name || 'You'}
                             </Typography>
                             <Avatar
                               alt={user?.name || 'User'}
                               src={user?.googleProfilePicture}
                               sx={{ width: '2.2vh', height: '2.2vh' }} // Converted from 20px
                             />
                           </Box>
                           <Typography variant="body2">{message.text}</Typography>
                         </Paper>
                      )}
                    </Box>
                  </Box>
                ))}
                {isLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Stack direction="row" spacing={1} alignItems="center"> {/* Theme spacing */}
                          <Avatar
                            alt="AI Agent"
                            src="/assets/Logo-part.svg"
                            sx={{ width: '2.2vh', height: '2.2vh' }} // Converted from 20px
                          />
                          <CircularProgress size={20} sx={{ color: 'action.active' }} /> {/* Kept size 20 (pixels) */}
                      </Stack>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Stack>
            </Box>

            <Box
              sx={{
                p: 1.5, // Theme spacing
                borderTop: '1px solid', // Kept 1px
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              {!isLoading && messages.length <= 1 && (
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1, mb: 1.5 }}> {/* Theme spacing */}
                  {defaultInputs.map((text) => (
                    <Chip
                      key={text}
                      label={text}
                      size="small"
                      variant="outlined"
                      onClick={() => handleSendMessage(text)}
                      sx={{ cursor: 'pointer', fontFamily: fontFamily }}
                    />
                  ))}
                </Box>
              )}
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Type your question here..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 5, // Unitless (theme or small px value)
                    '&.Mui-focused fieldset': {
                      borderColor: 'common.black',
                    },
                  },
                }}
              />
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
};

export default MiniBot;